import { inject, Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { OAuthService } from "angular-oauth2-oidc";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";

import { AuthenticationActions } from "../store/authentication";
import { AppState } from "../store/store";
import { authConfig } from "../utils/auth-config";
import { validateRelativeUrl } from "../utils/url-security";
import { AuthenticationService } from "./authentication.service";
import { LocalStorageService } from "./browser-apis/local-storage.service";
import { WindowService } from "./browser-apis/window.service";

@Injectable({ providedIn: "root" })
export class ExternalAuthenticationService
  extends AuthenticationService
  implements OnDestroy
{
  private oauthService = inject(OAuthService);
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  private windowService = inject(WindowService);
  private store = inject<Store<AppState>>(Store);

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public override isAuthenticated$ =
    this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public override isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();
  private ngUnsubscribe = new Subject<void>();
  private loginFlowInProgress = false;
  private loginFlowTimeout: any = null;
  private refreshInFlight: Promise<boolean> | null = null;
  // True while runInitialLoginSequence is executing. The token_received event
  // fires inside loadDiscoveryDocumentAndTryLogin during an OIDC callback and
  // would normally dispatch loginSuccess immediately — before product consistency
  // is confirmed. Deferring the dispatch to runInitialLoginSequence's continuation
  // (where shouldProceed is known) prevents marking the store authenticated with
  // a mismatched product before the redirect fires. Silent refresh also fires
  // token_received but outside the initial sequence, so the flag stays false
  // and those events dispatch directly as before.
  private isInitialSequenceRunning = false;

  private readonly PRODUCT_CLAIM = "https://claims.du.com/product";
  private readonly PRODUCT_KEY = "product";

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public override canActivateProtectedRoutes$: Observable<boolean> =
    combineLatest([this.isAuthenticated$, this.isDoneLoading$]).pipe(
      map(values => values.every(b => b))
    );

  private navigateToLoginPage() {
    void this.login();
  }

  constructor() {
    super();
    this.oauthService.events
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.isAuthenticatedSubject$.next(
          this.oauthService.hasValidAccessToken()
        );
      });

    this.oauthService.events
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(e => ["token_received"].includes(e.type))
      )
      .subscribe(() => {
        void this.oauthService.loadUserProfile();
        if (!this.isInitialSequenceRunning) {
          // Outside the initial sequence (e.g. silent refresh): dispatch directly.
          // During the initial sequence, runInitialLoginSequence dispatches after
          // confirming product consistency, so we skip the dispatch here.
          this.store.dispatch(AuthenticationActions.loginSuccess());
        }
      });

    this.oauthService.events
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(e => ["session_terminated", "session_error"].includes(e.type))
      )
      .subscribe(() => this.navigateToLoginPage());

    this.oauthService.events
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(e => ["silent_refresh_error"].includes(e.type))
      )
      .subscribe(() => {
        this.navigateToLoginPage();
      });

    this.oauthService.setupAutomaticSilentRefresh();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this.loginFlowTimeout) {
      clearTimeout(this.loginFlowTimeout);
    }
  }

  public override runInitialLoginSequence(): Promise<void> {
    this.isInitialSequenceRunning = true;
    const localStorageProduct = this.localStorageService.getItem(
      this.PRODUCT_KEY
    );
    const customQueryParams = { ...authConfig.customQueryParams } as any;
    if (
      localStorageProduct &&
      localStorageProduct !== "null" &&
      localStorageProduct !== "undefined"
    ) {
      customQueryParams.product = localStorageProduct;
    }
    this.oauthService.configure({
      ...authConfig,
      customQueryParams,
    });
    if (location.hash) {
      console.log("Encountered hash fragment, plotting as table...");
      console.table(
        location.hash
          .substr(1)
          .split("&")
          .map(kvp => kvp.split("="))
      );
    }

    // 0. LOAD CONFIG:
    // First we have to check to see how the IdServer is
    // currently configured:
    return this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        const currentProduct = this.getProduct();
        const storageProduct = this.localStorageService.getItem(
          this.PRODUCT_KEY
        );
        if (
          this.oauthService.hasValidAccessToken() &&
          (currentProduct === storageProduct || storageProduct === null)
        ) {
          return true;
        }
        // Redirect in flight. isDoneLoading$ is still set to true below so guards
        // are not wedged; the page navigates away before any subscriber acts on it.
        void this.login();
        return false;
      })

      .then(shouldProceed => {
        // Always unblock guards: without this, a product-switch reload (valid
        // token but mismatched stored product) calls login() and returns false,
        // leaving isDoneLoading$ false indefinitely and wedging
        // canActivateProtectedRoutes$. The page navigates away via the login
        // redirect before any subscriber fires, so the brief true emission is
        // harmless. loginSuccess and state navigation are still gated on
        // shouldProceed to avoid redundant API calls before the redirect lands.
        this.isDoneLoadingSubject$.next(true);
        this.isInitialSequenceRunning = false;
        if (!shouldProceed) return;
        // Dispatch loginSuccess here for all success paths:
        // - Pre-existing valid session: token_received did not fire, so this
        //   is the only dispatch.
        // - OIDC callback: token_received fired but skipped the dispatch because
        //   isInitialSequenceRunning was true, so this is still the only dispatch.
        this.store.dispatch(AuthenticationActions.loginSuccess());

        // Check for the strings 'undefined' and 'null' just to be sure. Our current
        // login(...) should never have this, but in case someone ever calls
        // initImplicitFlow(undefined | null) this could happen.
        if (
          this.oauthService.state &&
          this.oauthService.state !== "undefined" &&
          this.oauthService.state !== "null"
        ) {
          let stateUrl = this.oauthService.state;
          if (!stateUrl.startsWith("/")) {
            stateUrl = decodeURIComponent(stateUrl);
          }
          void this.router.navigateByUrl(stateUrl);
        }
      })
      .catch(() => {
        this.isDoneLoadingSubject$.next(true);
        this.isInitialSequenceRunning = false;
      });
  }

  public override isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  public override login(targetUrl?: string): Promise<boolean> {
    if (this.loginFlowInProgress) {
      return Promise.resolve(false);
    }

    this.loginFlowInProgress = true;
    this.oauthService.initLoginFlow(targetUrl || this.router.url);

    this.loginFlowTimeout = setTimeout(() => {
      this.loginFlowInProgress = false;
    }, 5000);

    return Promise.resolve(true);
  }

  public override logout() {
    this.oauthService.logOut();
  }

  public override refresh(): Promise<boolean> {
    // Coalesce concurrent calls: parallel 401s each trigger refresh()
    // independently. silentRefresh() is not re-entrant — concurrent calls
    // conflict and can invalidate each other's freshly-issued tokens.
    // Returning the same in-flight promise keeps it to one iframe refresh.
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this.oauthService
      .silentRefresh()
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        this.refreshInFlight = null;
      });
    return this.refreshInFlight;
  }
  public override hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  public override accessToken() {
    return new Promise<string>(resolve => {
      resolve(this.oauthService.getAccessToken());
    });
  }

  private getProduct(): string | null {
    const claims = this.oauthService.getIdentityClaims();
    if (claims && claims[this.PRODUCT_CLAIM]) {
      return claims[this.PRODUCT_CLAIM] as string;
    }
    return null;
  }

  public override switchProduct(product: string) {
    this.localStorageService.setItem(this.PRODUCT_KEY, product);
    const pathname = this.windowService.getLocationPathname();

    const safeReloadUrl = validateRelativeUrl(pathname);
    this.windowService.locationReplace(safeReloadUrl);
  }

  // These normally won't be exposed from a service like this, but
  // for debugging it makes sense.
  public get accessToken2() {
    return this.oauthService.getAccessToken();
  }
  public get refreshToken() {
    return this.oauthService.getRefreshToken();
  }
  public override get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }
  public get idToken() {
    return this.oauthService.getIdToken();
  }
  public get logoutUrl() {
    return this.oauthService.logoutUrl;
  }
}
